import { getCombinatoricsKeyboardLayout } from 'zycommon-web/utils/symbolic-tools/combinatorics/combinatorics-math-live';
import { getFunctionsKeyboardLayout } from 'zycommon-web/utils/symbolic-tools/functions/functions-math-live';
import { buildCommonKeys } from 'zycommon-web/utils/symbolic-tools/math-live-keyboard-shared';
import { getNumberAndUnitKeyboardLayout } from 'zycommon-web/utils/symbolic-tools/number-and-units/number-and-units-math-live';
import { getNumericVariablesKeyboardLayout } from 'zycommon-web/utils/symbolic-tools/numeric-variables/numeric-variables-math-live';
import { getPropositionalLogicKeyboardLayout } from 'zycommon-web/utils/symbolic-tools/propositional-logic/propositional-logic-math-live';
import { getSymbolicEquivalenceKeyboardLayout } from 'zycommon-web/utils/symbolic-tools/symbolic-equivalence/symbolic-equivalence-math-live';
import { getSymbolicNumberKeyboardLayout } from 'zycommon-web/utils/symbolic-tools/symbolic-number/symbolic-number-math-live';

import type { BuilderPropertyRecord } from '../tool-settings';
import { builderPropsToKeyboardContext, resolveNotation, resolveRequireUnits, type EditorAtomicSymbol } from './mathlive-question-context';

/** Same options as `virtual_keyboard_layout_name` dropdown in legacy `short-answer-question-builder-tools.js`. */
export const VIRTUAL_KEYBOARD_LAYOUT_OPTIONS = [
    'Kitchen sink',
    'Algebra Ch6,7,8,10',
    'Algebra with logs',
    'Callister',
    'Gerhart',
    'Groover',
    'Koretsky',
    'Liberatore',
    'LSAS',
    'Moran',
    'Montgomery',
    'Nise',
] as const;

export type MathVirtualKeyboardLayout = { rows: unknown[] } | readonly string[] | string[];

/**
 * Same branch order as `keyboardLayout` in zb-math-live.js.
 */
export function getMathVirtualKeyboardLayoutForShortAnswer(
    builder: BuilderPropertyRecord,
    {
        expectedString = '',
        editorAtomicSymbols = [],
        isChallenge = false,
    }: {
        expectedString?: string;
        editorAtomicSymbols?: EditorAtomicSymbol[];
        isChallenge?: boolean;
    } = {},
): MathVirtualKeyboardLayout {
    const notation = resolveNotation(builder);
    const requireUnits = resolveRequireUnits(builder);
    const question = builderPropsToKeyboardContext(builder, { expectedString });
    const commonKeys = buildCommonKeys({ isChallenge });
    const rows: unknown[] = [];

    if (notation === 'combinatorics') {
        rows.push(...getCombinatoricsKeyboardLayout(commonKeys));
    } else if (notation === 'functions') {
        rows.push(...getFunctionsKeyboardLayout(commonKeys, question));
    } else if (notation === 'propositional logic') {
        rows.push(...getPropositionalLogicKeyboardLayout(commonKeys, question, editorAtomicSymbols));
    } else if (notation === 'number and units' || requireUnits) {
        rows.push(...getNumberAndUnitKeyboardLayout(commonKeys, question));
    } else if (notation === 'numeric variables') {
        return getNumericVariablesKeyboardLayout(commonKeys, question);
    } else if (notation === 'symbolic equivalence') {
        return getSymbolicEquivalenceKeyboardLayout(commonKeys, question);
    } else {
        rows.push(...getSymbolicNumberKeyboardLayout(commonKeys, question));
    }

    return { rows };
}
